import { ESdChangeReason } from "@kuika/kuika-cl-model";
import { IWithSDProps, withSD } from "../../../shared/hoc/screen-design-consumer";
import { PureSdComponent } from "../../../shared/pure-sd";
import { KModal } from "../../ui-kit/k-modal/k-modal";
import { KModalFooter } from "../../ui-kit/k-modal/k-modal-footer";
import KuikaSmallText from "../../ui-kit/typography/SmallText/SmallText";
import { DataSources } from "../left-panel/datasource-panel/datasources";
import { DocumentParametersProperties } from "../right-panel/document-parameters-panel/document-parameters-properties";
import { DocumentParametersSection } from "./document-parameters-section";
import "./document-parameters-modal.scss";

export interface IDocumentParametersModalProps {}

export interface IDocumentParametersModalState {}

/**
 * The Document Parameters tool as a popup, opened from a Document component's properties (KOA-17254).
 *
 * <b>Why the whole tool and not just the table.</b> A parameter row is a tag and a source; the sources — declared
 * document inputs and the added source actions — are authored on the other surface. A popup with only the table
 * would let someone name `<SIRKET_ADI>` and then have nothing to point it at, which is the half-feature the
 * separate tool already exists to avoid. So both halves are here, side by side, and both are the same components
 * the tool renders — not a reduced copy that would drift from it.
 *
 * <b>Why the tool still exists.</b> This is the shortcut from a document that needs a parameter; the tool is where
 * the project's parameters are surveyed as a whole, and it is reachable from the sources tree without a screen
 * open. Neither replaces the other.
 *
 * <b>Why the actions list is here and not a link out.</b> The action a parameter needs often does not exist yet —
 * the tag is invented at the same moment as the query that fills it. Sending the user to another tool to write that
 * query loses the popup and the thought. The custom-actions list is used rather than the sources tree because it
 * opens its own editor modal: the sources tree defers to the data diagram canvas, which is not mounted here, so its
 * "New Select Action" would set an editing state with nothing on screen.
 *
 * <b>What made this possible.</b> Every surface underneath — the action picker, the action panel, the symbol picker
 * engine, the shared action-definition filter and the action write path — decided what to offer by asking whether
 * `dashboardState === documentParameters`. Over the design canvas that answer is no, so rendering this popup
 * unchanged would have produced an empty source-action list, a symbol picker with no parameter items, a filter
 * offering navigation actions that cannot supply a value, and — the dangerous one — an added action written onto
 * the selected Document component's own event. They now ask `isDocumentParameterAuthoringActive()`, which is true
 * on either host.
 */
class DocumentParametersModal extends PureSdComponent<
  IDocumentParametersModalProps & IWithSDProps,
  IDocumentParametersModalState
> {
  private close = (): void => {
    this.props.sd.closeDocumentParametersPopup();
  };

  render() {
    if (!this.props.sd?.isDocumentParametersPopupOpen) return null;

    return (
      <KModal
        showModal
        closeModal={this.close}
        width="min(1440px, 94vw)"
        centered
        showFullScreenButton
        className="kuika-document-parameters-modal"
        header="Document Parameters"
        footer={
          <KModalFooter submitLabel="Close" onSubmit={this.close} submitTestId="documentParametersModalCloseButton">
            {/* The table has its own Save; the right half commits per edit. Saying so prevents the reasonable
                assumption that closing the popup is what commits. */}
            <KuikaSmallText className="kuika-document-parameters-modal__footer-note">
              Inputs and source actions save as you edit them. The parameter table has its own Save.
            </KuikaSmallText>
          </KModalFooter>
        }
      >
        <div className="kuika-document-parameters-modal__notice">
          Project-wide. Every document in this project uses these definitions.
        </div>
        <div className="kuika-document-parameters-modal__body">
          <nav className="kuika-document-parameters-modal__pane kuika-document-parameters-modal__actions">
            <header className="kuika-document-parameters-modal__pane-head">
              <span className="kuika-document-parameters-modal__eyebrow">Actions</span>
              <span className="kuika-document-parameters-modal__hint">
                Browse the project&apos;s actions, or write a new query without leaving this dialog.
              </span>
            </header>
            <div className="kuika-document-parameters-modal__pane-body">
              <DataSources />
            </div>
          </nav>

          <section className="kuika-document-parameters-modal__pane">
            <header className="kuika-document-parameters-modal__pane-head">
              <span className="kuika-document-parameters-modal__eyebrow">Parameters</span>
              <span className="kuika-document-parameters-modal__hint">
                Each tag written in a document, and where its value comes from.
              </span>
            </header>
            <div className="kuika-document-parameters-modal__pane-body">
              <DocumentParametersSection projectId={this.props.sd?.project?.id ?? ""} />
            </div>
          </section>

          <aside className="kuika-document-parameters-modal__pane kuika-document-parameters-modal__sources">
            <header className="kuika-document-parameters-modal__pane-head">
              <span className="kuika-document-parameters-modal__eyebrow">Sources</span>
              <span className="kuika-document-parameters-modal__hint">
                What a value can be built from — declared inputs and the actions that fetch rows.
              </span>
            </header>
            <div className="kuika-document-parameters-modal__pane-body">
              <DocumentParametersProperties />
            </div>
          </aside>
        </div>
      </KModal>
    );
  }

  checkNeedRender = (changeReason: ESdChangeReason): boolean => {
    return (
      changeReason === ESdChangeReason.OtherSetStates ||
      changeReason === ESdChangeReason.DiActionUpdated ||
      changeReason === ESdChangeReason.ChangeSelectedDiByTools ||
      changeReason === ESdChangeReason.ChangeSelectedDiByCanvasFrame
    );
  };
}

const documentParametersModal = withSD<IDocumentParametersModalProps>(DocumentParametersModal);
export { documentParametersModal as DocumentParametersModal };
